/** Plain-English text for QuickBooks codes the server sends back. */

/** Why a visit wasn't sent (QbSyncResult.skipped[].reason). Anything else is Intuit's own message. */
const SKIP_TEXT: Record<string, string> = {
  person_not_mapped: "This person has no QuickBooks ID. Add it on their page, then send again.",
  quickbooks_unavailable: "Not sent: QuickBooks was unavailable. Try sending again later.",
  reconnect_needed: "Not sent: the QuickBooks connection has expired. Connect QuickBooks again.",
  limit_reached_sync_again: "Not sent yet: too many visits for one go. Press Send again for the rest.",
};

export function skipReasonText(reason: string): string {
  return SKIP_TEXT[reason] ?? `QuickBooks said: ${reason}`;
}

/** Why connecting failed (/admin/exports?qb=error&reason=…). */
const CONNECT_ERROR_TEXT: Record<string, string> = {
  signed_out: "You were signed out. Sign in, then connect again.",
  denied: "Access wasn't allowed in QuickBooks. Connect again and choose Connect (not Cancel).",
  expired_state: "The connection took too long. Please try again.",
  bad_state: "The connection took too long or was started in another tab. Please try again.",
  missing_code: "QuickBooks didn't say which company to connect. Please try again and choose a company.",
  intuit_error: "QuickBooks reported a problem. Please try again in a few minutes.",
  token_exchange_failed:
    "QuickBooks didn't accept the connection. Please try again, and make sure you choose the right company.",
  server_error: "Something went wrong saving the connection. Please try again.",
};

export const CONNECT_ERROR_FALLBACK =
  "QuickBooks didn't connect. Please try again, and make sure you choose the right company.";

export function connectErrorText(reason: string | null): string {
  return (reason && CONNECT_ERROR_TEXT[reason]) || CONNECT_ERROR_FALLBACK;
}

/** Groups skipped visits by message so a long list stays readable. */
export function groupSkipped(
  skipped: { visitId: string; reason: string }[],
): { text: string; visitIds: string[] }[] {
  const by = new Map<string, string[]>();
  for (const k of skipped) {
    const text = skipReasonText(k.reason);
    by.set(text, [...(by.get(text) ?? []), k.visitId]);
  }
  return [...by.entries()].map(([text, visitIds]) => ({ text, visitIds }));
}
